import React from 'react';
import { Link } from 'react-router-dom';
import { SEOHead, FAQSchema, CourseSchema } from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Palette, Users, Clock, Star, CheckCircle, ArrowRight, MapPin, Calendar, Shield, Phone } from 'lucide-react';

const YouthArtClassesSanAntonio = () => {
  const faqs = [
    {
      question: "What ages do your kids art classes serve?",
      answer: "Our Coppini Kids program serves young artists ages 5–18. Students are grouped by grade level (Kinder–2nd, 3rd–5th, 6th–8th, and 9th–12th) so every child receives age-appropriate instruction."
    },
    {
      question: "Do kids need prior art experience to join?",
      answer: "No prior experience is needed. Our kids art classes welcome complete beginners and provide structured instruction in fundamental drawing and painting skills from day one."
    },
    {
      question: "What will my child learn in art classes?",
      answer: "Students learn classical drawing techniques, color theory, composition, and various media including graphite, charcoal, watercolor, acrylic, pastel, and oil paint. Our curriculum builds strong observational skills and artistic confidence."
    },
    {
      question: "How much do kids art classes cost?",
      answer: "Tuition varies by session length and grade level. We offer semester-based enrollment with Fall, Spring, and Summer sessions. Contact us at (210) 530-8445 or visit our Kids Classes page for current pricing."
    },
    {
      question: "How small are the class sizes?",
      answer: "We limit classes to 8–10 students maximum. This small group size ensures every child gets personal attention from the instructor and a productive learning environment."
    },
    {
      question: "Where are classes held?",
      answer: "All kids art classes are held at The Coppini Academy of Fine Art, 115 Melrose Place, San Antonio, TX 78212, in the Monte Vista historic neighborhood. Free parking is available on site."
    },
    {
      question: "What days and times are kids classes?",
      answer: "We offer after-school classes on Wednesdays and Thursdays (4–5:30 PM) and weekend options. Visit our schedule page or call (210) 530-8445 for the current semester's class times."
    },
    {
      question: "Are the classes structured or free-form?",
      answer: "Our kids art classes follow a structured curriculum based on classical training principles. While creativity is encouraged, we believe strong foundational skills give students the tools to express themselves effectively."
    },
    {
      question: "Is The Coppini Academy a nonprofit?",
      answer: "Yes. The Coppini Academy of Fine Art is a 501(c)(3) nonprofit organization founded in 1945 and incorporated in 1952. Our mission is to preserve and promote classical fine art education in San Antonio."
    }
  ];

  return (
    <main className="min-h-screen bg-white">
      <SEOHead
        title="Kids Art Classes San Antonio | Youth Art Programs Ages 5–18 | Coppini Academy"
        description="Kids art classes in San Antonio for ages 5–18. Classical drawing & painting in small groups of 8–10 students. After-school & weekend options. Nonprofit art school since 1945. (210) 530-8445."
        keywords="kids art classes San Antonio, kids art classes, art classes for kids San Antonio TX, children art lessons San Antonio, youth art programs San Antonio, kids drawing classes, art school for kids San Antonio, after school art classes San Antonio"
        canonicalPath="/kids-art-classes-san-antonio"
      />
      <FAQSchema faqs={faqs} />
      <CourseSchema
        name="Kids Art Classes San Antonio"
        description="Classical drawing and painting classes for kids ages 5–18 in San Antonio, TX. Small groups, professional instructors, after-school and weekend sessions."
      />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900 text-white py-20 lg:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-1 bg-cafa-red/20 text-cafa-gold text-sm font-medium rounded-full mb-6">
              Coppini Kids Program — San Antonio
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold mb-6 leading-tight">
              Kids Art Classes<br className="hidden md:block" /> in San Antonio
            </h1>
            <p className="text-xl text-stone-300 mb-3 leading-relaxed">
              Classical drawing and painting for young artists ages 5–18. Small groups of 8–10 students, 
              professional instructors, and a structured curriculum that builds real skills.
            </p>
            <p className="text-stone-400 mb-8">
              After-school (Wed/Thu 4–5:30 PM) and weekend options available. Serving San Antonio since 1945.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="bg-cafa-red hover:bg-cafa-red-900 text-white">
                <Link to="/classes/kids">See Kids Class Schedule <ArrowRight className="ml-2" size={18} /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                <a href="tel:+12105308445">(210) 530-8445</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Art Education for Kids */}
      <section className="py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mb-4">
              Why Art Education Matters for Young People
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              Art education develops skills that benefit children throughout their lives, 
              from improved observation to greater creative confidence.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Palette,
                title: "Creative Development",
                desc: "Nurture imagination and self-expression through structured artistic practice"
              },
              {
                icon: Users,
                title: "Confidence Building",
                desc: "Develop pride in accomplishment as skills visibly improve"
              },
              {
                icon: Clock,
                title: "Focus & Patience",
                desc: "Learn to work carefully and see projects through to completion"
              },
              {
                icon: Star,
                title: "Visual Literacy",
                desc: "Train the eye to observe and analyze the visual world"
              }
            ].map((benefit, idx) => (
              <Card key={idx} className="border-stone-200 hover:shadow-lg transition-shadow">
                <CardContent className="p-6 text-center">
                  <benefit.icon className="mx-auto mb-4 text-cafa-red" size={40} />
                  <h3 className="font-semibold text-stone-900 mb-2">{benefit.title}</h3>
                  <p className="text-stone-600 text-sm">{benefit.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Coppini Kids Program */}
      <section className="py-16 lg:py-20 bg-stone-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mb-6">
                The Coppini Kids Program
              </h2>
              <p className="text-lg text-stone-600 mb-4 leading-relaxed">
                Coppini Kids brings the same quality of instruction adults receive at our 
                academy, adapted for younger learners ages 5–18. We believe children deserve 
                serious art education—not just craft projects, but real classical training in 
                drawing, painting, and observation.
              </p>
              <p className="text-lg text-stone-600 mb-6 leading-relaxed">
                Classes are capped at 8–10 students so every child receives personal 
                attention. Students progress through a structured curriculum that builds 
                foundational skills semester by semester.
              </p>
              <div className="space-y-3">
                {[
                  "Small class sizes — 8 to 10 students max",
                  "Ages 5–18, grouped by grade level",
                  "Classical drawing and painting curriculum",
                  "Professional artist instructors",
                  "Semester-based enrollment (Fall, Spring, Summer)",
                  "Supply list provided — no extra purchases required",
                  "End-of-semester student art exhibition"
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center">
                    <CheckCircle className="text-cafa-red mr-3 flex-shrink-0" size={20} />
                    <span className="text-stone-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-cafa-red/10 rounded-lg p-8">
              <h3 className="text-xl font-semibold text-stone-900 mb-4">Program Details</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <Users className="text-cafa-red mr-3 mt-1" size={20} />
                  <div>
                    <p className="font-medium text-stone-900">Ages 5–18</p>
                    <p className="text-stone-600 text-sm">Grouped by grade level: K–2nd, 3rd–5th, 6th–8th, 9th–12th</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Calendar className="text-cafa-red mr-3 mt-1" size={20} />
                  <div>
                    <p className="font-medium text-stone-900">Semester Enrollment</p>
                    <p className="text-stone-600 text-sm">Fall, Spring, and Summer sessions</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Clock className="text-cafa-red mr-3 mt-1" size={20} />
                  <div>
                    <p className="font-medium text-stone-900">After-School & Weekends</p>
                    <p className="text-stone-600 text-sm">Wed/Thu 4–5:30 PM; weekend options available</p>
                  </div>
                </div>
              </div>
              <Button asChild className="w-full mt-6 bg-cafa-red hover:bg-cafa-red-900">
                <Link to="/classes/kids">View Schedule & Enroll</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* What Students Learn */}
      <section className="py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mb-4 text-center">
            What Young Artists Learn
          </h2>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto text-center mb-12">
            Our youth curriculum covers fundamental skills that build a strong artistic foundation.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Drawing Fundamentals",
                skills: ["Line quality and control", "Basic shapes and forms", "Proportion and measurement", "Light and shadow"]
              },
              {
                title: "Observational Skills",
                skills: ["Drawing from life", "Still life studies", "Outdoor sketching", "Visual analysis"]
              },
              {
                title: "Color Theory",
                skills: ["Color wheel basics", "Mixing colors", "Warm and cool colors", "Color harmony"]
              },
              {
                title: "Painting Techniques",
                skills: ["Brush handling", "Paint consistency", "Layering color", "Finishing work"]
              },
              {
                title: "Composition",
                skills: ["Arranging elements", "Focal points", "Balance and unity", "Creative choices"]
              },
              {
                title: "Art History",
                skills: ["Famous artists", "Art movements", "Museum visits", "Artistic inspiration"]
              }
            ].map((area, idx) => (
              <Card key={idx} className="border-stone-200">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-stone-900 mb-4">{area.title}</h3>
                  <ul className="space-y-2">
                    {area.skills.map((skill, i) => (
                      <li key={i} className="text-stone-600 text-sm flex items-center">
                        <CheckCircle size={14} className="text-cafa-red mr-2 flex-shrink-0" />
                        {skill}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Parent Trust Section */}
      <section className="py-16 lg:py-20 bg-stone-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mb-4">
              What Parents Say
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              Families across San Antonio trust Coppini Kids for quality after-school art education.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                quote: "My daughter has been in the Coppini Kids program for two semesters. The improvement in her drawings is remarkable — she's learning real technique, not just crafts.",
                author: "San Antonio Parent",
                detail: "Parent of 10-year-old"
              },
              {
                quote: "Small class sizes make such a difference. The instructor knows my son by name and adjusts lessons to his pace. He looks forward to class every week.",
                author: "Monte Vista Neighborhood Parent",
                detail: "Parent of 8-year-old"
              },
              {
                quote: "We've tried other art programs in San Antonio. None compare to the Coppini Academy's level of instruction. This is serious art education for kids.",
                author: "San Antonio Parent",
                detail: "Parent of 14-year-old"
              }
            ].map((t, i) => (
              <Card key={i} className="border-stone-200">
                <CardContent className="p-6">
                  <div className="flex mb-3">
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} size={16} className="text-cafa-gold fill-cafa-gold" />
                    ))}
                  </div>
                  <p className="text-stone-700 italic mb-4">&ldquo;{t.quote}&rdquo;</p>
                  <p className="text-sm font-semibold text-stone-900">{t.author}</p>
                  <p className="text-xs text-stone-500">{t.detail}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What to Expect — Parent Guide */}
      <section className="py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mb-4">
              What to Expect on Your Child&apos;s First Day
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              We make enrollment easy and first visits welcoming for both kids and parents.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: "1", title: "Supply List Provided", desc: "We email you a short supply list before the semester begins. No guessing what to buy." },
              { step: "2", title: "Drop-Off at Studio", desc: "Park and walk in at 115 Melrose Place. Instructors greet students at the door." },
              { step: "3", title: "45-Min Structured Class", desc: "Each session follows a lesson plan — no wasted time. Students work from the first minute." },
              { step: "4", title: "Pick Up & Show Off", desc: "Students bring home their work every session. See real progress from week one." }
            ].map((s, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 rounded-full bg-cafa-red text-white flex items-center justify-center text-xl font-bold mx-auto mb-4">{s.step}</div>
                <h3 className="font-semibold text-stone-900 mb-2">{s.title}</h3>
                <p className="text-stone-600 text-sm">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location & Contact */}
      <section className="py-16 lg:py-20 bg-stone-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-serif mb-6">
                A Safe, Inspiring Place to Learn
              </h2>
              <p className="text-stone-300 mb-6 leading-relaxed">
                Our Monte Vista studio is a welcoming, supervised environment purpose-built 
                for young artists. Parents consistently note the organized, professional 
                atmosphere — and kids love the dedicated art space.
              </p>
              <div className="flex items-start mb-3">
                <Shield className="text-cafa-red mr-3 mt-1 flex-shrink-0" size={20} />
                <p className="text-stone-300 text-sm">Students are supervised by professional artist instructors at all times during class.</p>
              </div>
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <MapPin className="text-cafa-red mr-3 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <p className="font-medium">The Coppini Academy of Fine Art</p>
                    <p className="text-stone-400">115 Melrose Place</p>
                    <p className="text-stone-400">San Antonio, TX 78212</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Phone className="text-cafa-red mr-3 flex-shrink-0" size={20} />
                  <a href="tel:+12105308445" className="text-stone-300 hover:text-white font-medium">(210) 530-8445</a>
                </div>
              </div>
              <Button asChild className="bg-cafa-red hover:bg-cafa-red-900">
                <Link to="/contact">Contact Us About Kids Classes</Link>
              </Button>
            </div>
            <div className="space-y-6">
              <div className="rounded-lg overflow-hidden">
                <iframe
                  src="https://maps.google.com/maps?q=115+Melrose+Place,+San+Antonio,+TX+78212&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="200"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Coppini Academy Location - Youth Art Classes San Antonio"
                  className="grayscale"
                ></iframe>
              </div>
              <div className="bg-stone-800 rounded-lg p-8">
                <h3 className="text-xl font-semibold mb-4">Enroll Your Child</h3>
              <p className="text-stone-400 mb-6">
                Spaces in our youth program are limited. We encourage early registration 
                to secure your child&apos;s spot.
              </p>
              <div className="space-y-3">
                <Button asChild className="w-full bg-white text-stone-900 hover:bg-stone-100">
                  <Link to="/classes/kids">View Youth Class Schedule</Link>
                </Button>
                <Button asChild variant="outline" className="w-full border-white text-white hover:bg-white/10">
                  <Link to="/register-class">Register Now</Link>
                </Button>
              </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mb-8 text-center">
            Youth Program FAQs
          </h2>
          <div className="space-y-6">
            {faqs.map((faq, idx) => (
              <div key={idx} className="border-b border-stone-200 pb-6">
                <h3 className="text-lg font-semibold text-stone-900 mb-2">{faq.question}</h3>
                <p className="text-stone-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-cafa-red text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-serif mb-4">
            Enroll Your Child in San Antonio&apos;s Premier Kids Art Program
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Small classes. Professional instructors. Real skills. Serving San Antonio families since 1945.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-cafa-red hover:bg-stone-100">
              <Link to="/classes/kids">See Kids Class Schedule</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
              <a href="tel:+12105308445">Call (210) 530-8445</a>
            </Button>
          </div>
        </div>
      </section>
    </main>
  );
};

export default YouthArtClassesSanAntonio;
